import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Box, Container, Link, TextField, Typography } from '@material-ui/core';

import { useDispatch } from 'react-redux';
import { setProfile } from '../store/actions/profile';
import { getSubscriptionInfoAction } from '../store/actions/subscription';
import api from '../api';

import { useAlert } from '../hooks';
import { getErrorText } from '../utils';
import { LoadingButton } from '../components/common';

const initialValues = {
	email: '',
	password: '',
	recaptcha: '',
};

const validationSchema = Yup.object().shape({
	email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
	password: Yup.string().max(255).required('Password is required'),
	recaptcha: Yup.string().required(),
});

export default function LoginForm() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const callAlert = useAlert();

	const onSubmit = async (reqData, formikContext) => {
		const { resetForm } = formikContext;
		try {
			const { data: resData } = await api.signin(reqData);
			if (resData?.status === 'ok') {
				const {
					token,
					user,
					subscription: { state },
				} = resData;

				localStorage.token = token;
				resetForm();
				dispatch(setProfile(user));
				dispatch(getSubscriptionInfoAction());
				if (state === 'free') {
					navigate('/admin/pricing', { replace: true });
				} else {
					navigate('/admin/dashboard', { replace: true });
				}
			} else {
				callAlert(getErrorText(resData?.message), 'error');
				console.error(resData);
			}
		} catch (error) {
			console.error(error);
			callAlert('An error has occurred. Please contact us via the feedback form.', 'error');
		}
	};

	return (
		<>
			<Helmet>
				<title>Login | Parcels API</title>
			</Helmet>
			<Box
				sx={{
					backgroundColor: 'background.default',
					display: 'flex',
					flexDirection: 'column',
					minHeight: 'calc(100vh - 154px)',
					justifyContent: 'center',
				}}
			>
				<Container maxWidth="sm">
					<Formik {...{ initialValues, validationSchema, onSubmit }}>
						{({
							errors,
							handleBlur,
							handleChange,
							handleSubmit,
							isSubmitting,
							touched,
							values,
							setFieldValue,
						}) => (
							<form onSubmit={handleSubmit}>
								<Box sx={{ mb: 3 }}>
									<Typography align="center" color="textPrimary" variant="h2">
										Sign in
									</Typography>
									<Typography
										align="center"
										color="textSecondary"
										gutterBottom
										variant="body2"
										style={{ marginTop: 10 }}
									>
										Sign in to Parcels App API Dashboard
									</Typography>
								</Box>
								<TextField
									error={Boolean(touched.email && errors.email)}
									fullWidth
									helperText={touched.email && errors.email}
									label="Email Address"
									margin="normal"
									name="email"
									onBlur={handleBlur}
									onChange={handleChange}
									type="email"
									value={values.email}
									variant="outlined"
								/>
								<TextField
									error={Boolean(touched.password && errors.password)}
									fullWidth
									helperText={touched.password && errors.password}
									label="Password"
									margin="normal"
									name="password"
									onBlur={handleBlur}
									onChange={handleChange}
									type="password"
									value={values.password}
									variant="outlined"
								/>
								<Box
									sx={{
										py: 2,
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
									}}
								>
									<ReCAPTCHA
										className="g-recaptcha"
										sitekey={process.env.REACT_APP_RECAPTCHA_KEY || ''}
										onChange={(response) => setFieldValue('recaptcha', response)}
									/>
									<LoadingButton
										style={{ marginTop: '20px' }}
										color="primary"
										disabled={
											isSubmitting ||
											Object.keys(errors)?.length > 0 ||
											Object.values(values).some((i) => !i)
										}
										fullWidth
										size="large"
										type="submit"
										variant="contained"
									>
										Sign in
									</LoadingButton>
								</Box>
								<Typography align="center" color="textSecondary" variant="body1">
									Don&apos;t have an account?{' '}
									<Link component={RouterLink} to="/register" variant="h6">
										Sign up
									</Link>
								</Typography>
							</form>
						)}
					</Formik>
				</Container>
			</Box>
		</>
	);
}
